import React from 'react';
import { Stack, Typography, Paper, Alert } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { reorderSteps } from '../../../state';
import { StepListItem, AddStepButton } from '../../StepEditor';
import { useWorkflowValidationContext } from '../../shared';

export const StepsSection: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentWorkflow = useAppSelector((state) => state.workflow.currentWorkflow);
  const { errors, showErrors } = useWorkflowValidationContext();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!currentWorkflow) return null;

  const steps = currentWorkflow.steps;
  const dragIds = steps.map((_, i) => `step-${i}`);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = dragIds.indexOf(active.id as string);
    const newIndex = dragIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...steps];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    dispatch(reorderSteps(reordered));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: 1.5,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b' }}>
          Workflow Steps
        </Typography>
        <AddStepButton />
      </Stack>

      {showErrors && errors.steps && (
        <Alert severity="error" sx={{ mb: 2 }}>{errors.steps}</Alert>
      )}

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={dragIds} strategy={verticalListSortingStrategy}>
          <Stack spacing={2}>
            {steps.map((_, index) => (
              <StepListItem key={dragIds[index]} index={index} dragId={dragIds[index]} />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>
    </Paper>
  );
};

export default StepsSection;
